import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useTranslation } from './lib/i18n';
import ErrorBoundary from './components/ErrorBoundary';
import AppLayout from './components/AppLayout';
import Loading from './components/Loading';
import SigningOutOverlay from './components/SigningOutOverlay';

/**
 * React.lazy() wrapper that recovers from chunk-load failures (classic
 * deploy-time race: an old index.html / cached HTML response for a chunk
 * URL points the browser at code that no longer matches the live deploy).
 *
 * On dynamic-import failure, reload once — fresh index.html points at the
 * new chunk hashes. The guard is set ONLY on failure and cleared ONLY on
 * a successful import, so:
 *   - first failure → set guard → reload → success → clear guard
 *   - first failure → set guard → reload → second failure → throw to
 *     ErrorBoundary instead of looping infinitely
 */
const RELOAD_GUARD_KEY = 'ev_chunk_reload_done';
function lazyWithRetry(factory) {
  return React.lazy(async () => {
    try {
      const mod = await factory();
      try { sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch { /* private mode */ }
      return mod;
    } catch (err) {
      let alreadyRetried = false;
      try {
        alreadyRetried = !!sessionStorage.getItem(RELOAD_GUARD_KEY);
        if (!alreadyRetried) sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
      } catch { /* private mode — still try to reload once below */ }
      if (alreadyRetried) throw err;
      window.location.reload();
      // Stay pending so React keeps showing the Suspense fallback until reload takes over.
      return new Promise(() => {});
    }
  });
}

const Reader = lazyWithRetry(() => import('./pages/Reader'));
const DemoReader = lazyWithRetry(() => import('./pages/DemoReader'));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const Auth = lazyWithRetry(() => import('./pages/Auth'));
const DebugIdbDump = lazyWithRetry(() => import('./pages/DebugIdbDump'));
// Library is the largest page — lazy-loading drops main bundle ~150-200KB
// and saves ~300-500ms JS parse on low-end Android phones.
const Library = lazyWithRetry(() => import('./pages/Library'));
// Pre-auth marketing landing shown at "/" to logged-out visitors (lazy so
// logged-in users never download it).
const MarketingLanding = lazyWithRetry(() => import('./pages/MarketingLanding'));
import { installGlobalErrorReporter } from './lib/error-reporter';

// Install global error handlers at module load (before React renders)
installGlobalErrorReporter();

function ProtectedRoute({ children, unauthElement }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <Loading showToolbar={false} />;
  }

  if (!user) {
    // Most protected routes redirect logged-out visitors to the login flow.
    // The "/" index passes unauthElement={<MarketingLanding />} so the home
    // route shows the marketing landing instead (deep links still redirect).
    return unauthElement || <Navigate to="/auth?from=reader" replace />;
  }

  return children;
}

function AccountSwitchDialog() {
  const { accountMismatch, confirmAccountSwitch, dismissAccountSwitch } = useAuth();
  const { t } = useTranslation();
  if (!accountMismatch) return null;

  const desc = t('auth.switchDesc').replace('{email}', accountMismatch.extensionEmail);

  return (
    <div
      onClick={dismissAccountSwitch}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.45)', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12, padding: '1.5rem 2rem',
          maxWidth: 400, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          fontFamily: "'Source Sans 3', sans-serif",
        }}
      >
        <p style={{ fontSize: '1.1rem', fontWeight: 600, margin: '0 0 0.75rem', color: '#1a1a1a' }}>
          {t('auth.switchTitle')}
        </p>
        <p style={{ fontSize: '0.9rem', color: '#555', lineHeight: 1.5, margin: '0 0 1.25rem' }}>
          {desc}
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={dismissAccountSwitch}
            style={{
              padding: '0.5rem 1.25rem', borderRadius: 8, border: '1px solid #ccc',
              background: '#fff', cursor: 'pointer', fontSize: '0.9rem', color: '#555',
            }}
          >
            {t('auth.switchDismiss')}
          </button>
          <button
            type="button"
            onClick={confirmAccountSwitch}
            style={{
              padding: '0.5rem 1.25rem', borderRadius: 8, border: 'none',
              background: '#2D5A4A', color: '#fff', cursor: 'pointer', fontSize: '0.9rem',
            }}
          >
            {t('auth.switchConfirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ErrorBoundary>
        <AccountSwitchDialog />
        <SigningOutOverlay />
        <React.Suspense fallback={<Loading showToolbar={false} />}>
        <Routes>
          {/* Debug page: read-only IndexedDB dump for diagnosing data-state
              freezes. NOT protected by auth — uses ?key= soft guard so the
              user can access it even if their session/data is corrupted.
              See src/pages/DebugIdbDump.jsx. */}
          <Route path="/debug/idb-dump" element={<DebugIdbDump />} />
          <Route path="/auth" element={<Auth />} />
          <Route
            path="/read/:bookId"
            element={
              <ProtectedRoute>
                <ErrorBoundary
                  fallback={({ error, reset }) => (
                    <div style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      justifyContent: 'center', minHeight: '80vh', padding: '2rem',
                      fontFamily: "'Source Sans 3', sans-serif", color: '#333',
                    }}>
                      <h2>Failed to load reader</h2>
                      <p style={{ color: '#666', marginBottom: '1rem' }}>{error?.message}</p>
                      <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button onClick={reset} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>Retry</button>
                        <button onClick={() => window.location.href = '/'} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: 'none', background: '#2D5A4A', color: '#fff', cursor: 'pointer' }}>Library</button>
                      </div>
                    </div>
                  )}
                >
                  <Reader />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/demo/:slug"
            element={
              <ErrorBoundary
                fallback={({ error, reset }) => (
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', minHeight: '80vh', padding: '2rem',
                    fontFamily: "'Source Sans 3', sans-serif", color: '#333',
                  }}>
                    <h2>Failed to load demo</h2>
                    <p style={{ color: '#666', marginBottom: '1rem' }}>{error?.message}</p>
                    <button onClick={reset} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>Retry</button>
                  </div>
                )}
              >
                <DemoReader />
              </ErrorBoundary>
            }
          />
          {/* Home "/": logged-out → marketing landing; logged-in → Library shell. */}
          <Route element={<ProtectedRoute unauthElement={<MarketingLanding />}><AppLayout /></ProtectedRoute>}>
            <Route index element={<Library />} />
          </Route>
          <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route path="dashboard" element={<Dashboard />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </React.Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;

